<script setup lang="ts">
import Btn from '@ds/components/Btn/Btn.vue';
import { computed, ref } from 'vue';
import InputField from '@ds/components/InputField/InputField.vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import useErrorContent from '@shared/utils/useErrorContent';
import { useAllMerchantsStore } from '@apps/ops/src/store';
import router from '@apps/ops/src/router';

const { t } = useI18n();
const { getErrorInfo } = useErrorContent();
const allMerchantsStore = useAllMerchantsStore();
type State = 'default' | 'loading' | 'success' | 'error';

const options = {
	headers: {
		'Merchant-Code': process.env.VUE_APP_PAYAUT_MERCHANT_CODE,
		'Merchant-Division-Code': process.env.VUE_APP_PAYAUT_DIVISION_CODE,
		'Payaut-Populate-Code-Fields': 'generate',
	},
};

type KycTiers = 'LIGHT_KYC' | 'STANDARD_KYC' | 'ADVANCED_KYC';

const kycTiers: { value: KycTiers; label: string }[] = [
	{ value: 'LIGHT_KYC', label: 'Light KYC' },
	{ value: 'STANDARD_KYC', label: 'Standard KYC' },
	{ value: 'ADVANCED_KYC', label: 'Advanced KYC' },
];

const showNewMerchantModal = ref(false);
const currentState = ref<State>('default');
const currentError = ref('');

type RequestValues = {
	displayName: string;
	description: string | null;
	defaultKycTier: KycTiers;
};

const defaultRequestValues: RequestValues = {
	displayName: '',
	description: null,
	defaultKycTier: kycTiers[2].value,
};
const requestValues = ref({ ...defaultRequestValues });

const setTier = (value: KycTiers) => {
	requestValues.value.defaultKycTier = value;
};

const closeCreateMerchantModal = () => {
	showNewMerchantModal.value = false;
	requestValues.value = { ...defaultRequestValues };
	currentState.value = 'default';
	currentError.value = '';
};

const handleCreateMerchant = async () => {
	currentState.value = 'loading';
	try {
		const data = await axios.post(
			'accountowner/api/v2/accountowners/merchants',
			{
				displayName: requestValues.value.displayName,
				description: requestValues.value.description,
				defaultKycTier: requestValues.value.defaultKycTier,
			},
			options
		);
		if (data) {
			const merchantCode = data.data.code;
			await allMerchantsStore.fetchAllDivisions(merchantCode);
			const divisionCode = await allMerchantsStore.divisionsData[0].code;

			if (!!merchantCode && !!divisionCode) {
				/**
				 * Divisions are created asynchronously
				 * so some times are not available for the UI
				 * if they are redirect user to newly created
				 * merchant/division
				 */
				await router.replace({
					path: '/',
					query: {
						merchantCode,
						divisionCode,
					},
				});
			}
			currentState.value = 'success';
		}
	} catch (error) {
		getErrorInfo(error);
		if (error.response?.status === 400 || error.response?.status === 409)
			currentError.value = error.response.data.message;
		currentState.value = 'error';
	}
};

const generateMessage = computed(() => {
	if (currentState.value === 'error') {
		return currentError.value ? currentError.value : t('ops.createMerchant.labels.error');
	}
	if (currentState.value === 'success') return t('ops.createMerchant.labels.success');
	return '';
});
</script>

<template>
	<div class="create-merchant" style="display: inline" v-click-outside="closeCreateMerchantModal">
		<Btn
			class="ml-1"
			:ariaLabel="$t('ops.createMerchant.title')"
			:label="$t('ops.createMerchant.title')"
			icon-name="bx-plus"
			size="normal"
			variant="secondary"
			@click="showNewMerchantModal = !showNewMerchantModal"
		/>
		<form
			v-if="showNewMerchantModal"
			class="create-merchant-modal flex flex-col popover-container"
		>
			<InputField
				type="text"
				:label="$t('ops.createMerchant.displayName')"
				:disabled="currentState === 'success'"
				@input="(text) => (requestValues.displayName = text.toString().trim())"
			/>
			<InputField
				type="text"
				:label="$t('ops.createMerchant.description')"
				:disabled="currentState === 'success'"
				@input="(text) => (requestValues.description = text.toString().trim())"
			/>
			<div class="flex justify-around">
				<Btn
					v-for="tier in kycTiers"
					:key="tier.value"
					:label="tier.label"
					variant="secondary"
					size="small"
					:icon-name="tier.value === requestValues.defaultKycTier ? 'bx-check' : ''"
					:disabled="tier.value === requestValues.defaultKycTier"
					@click="setTier(tier.value)"
				/>
			</div>
			<div class="flex justify-end gap-2">
				<Btn
					@click="closeCreateMerchantModal"
					:label="currentState === 'success' ? $t('globals.close') : $t('globals.cancel')"
					variant="secondary"
				/>
				<Btn
					:label="$t('globals.create')"
					:disabled="requestValues.displayName === '' || currentState === 'success'"
					:is-loading="currentState === 'loading'"
					type="button"
					@click="handleCreateMerchant"
				/>
			</div>
			<div>
				<p
					v-if="currentState === 'error' || currentState === 'success'"
					:class="{
						'text-success-400': currentState === 'success',
						'text-error-400': currentState === 'error',
					}"
				>
					{{ generateMessage }}
				</p>
			</div>
		</form>
	</div>
</template>

<style scoped lang="scss">
.create-merchant {
	position: relative;
	z-index: $z-index-3;
}
.create-merchant-modal {
	width: 320px;
	max-height: 50vh;
	background-color: $color-neutral-50;
	border-radius: $border-radius-3;
	padding: $spacing-5;
	margin-top: $spacing-2;
	box-shadow: $box-shadow-popover;
	gap: $spacing-6;
}
</style>
